import { SecureLinkVerificationType } from '@/types/myVaultTypes';

export const REQUEST_TYPE_DB = { ACH: 'ach', WIRE: 'wire', INTL: 'intl' } as const;
export type RequestTypeType = (typeof REQUEST_TYPE_DB)[keyof typeof REQUEST_TYPE_DB];

export const REQUEST_MODE_DB = { REQUEST: 'request', VERIFY: 'verify' } as const;
export type RequestModeType = (typeof REQUEST_MODE_DB)[keyof typeof REQUEST_MODE_DB];

export const REQUEST_STATUS_DB = {
  SENT: 'sent',
  PENDING_VERIFICATION: 'pending_verification',
  COMPLETED: 'completed',
  EXPIRED: 'expired',
  REVOKED: 'revoked',
  CLOSED: 'closed',
} as const;
export type RequestStatusType = (typeof REQUEST_STATUS_DB)[keyof typeof REQUEST_STATUS_DB];

export const REQUEST_STAGE_DB = {
  OPEN: 'open',
  SUBMITTED: 'submitted',
  CREATED: 'created',
  CONFIRMED: 'confirmed',
} as const;
export type RequestStageType = (typeof REQUEST_STAGE_DB)[keyof typeof REQUEST_STAGE_DB];

export type RequestAudienceType = 'recipient' | 'proxy';
export type RequestViaType = 'email' | 'sms' | 'whatsapp' | 'telegram' | 'copy';
export type RequestExpiryType = '24h' | '7d' | '30d';

export type TemplateFieldMetaType = {
  key: string;
  label: string;
  placeholder: string;
  pattern: string | null;
  sensitive: boolean;
  optional: boolean;
  detailed_only: boolean;
};

export type VendorVaultCatalogType = {
  request_types: Record<
    RequestTypeType,
    { label: string; summary: string; title: string; description: string; fields: TemplateFieldMetaType[] | null }
  >;
  banks: Record<string, { label: string; draft: boolean; fields: TemplateFieldMetaType[] }>;
};

export type RecipientRequestType = {
  id: string;
  request_type: RequestTypeType;
  bank: string;
  template_label: string;
  template_draft: boolean;
  recipient_name: string;
  mode: RequestModeType;
  mode_label: string;
  audience: RequestAudienceType;
  audience_label: string;
  via: RequestViaType;
  sent_to: string;
  expiry_code: RequestExpiryType;
  expiry_label: string;
  expires_at: string;
  wire_confirmation: boolean;
  detailed: boolean;
  status: RequestStatusType;
  status_label: string;
  stage: RequestStageType;
  stage_label: string;
  archived: boolean;
  url: string;
  invited_contact: string;
  invited_at: string | null;
  date_created: string;
  first_opened_at: string | null;
  completed_at: string | null;
  revoked_at: string | null;
  closed_at: string | null;
  created_in_bank_at: string | null;
  confirmation_sent_at: string | null;
  confirmation_reference: string;
  confirmation_amount: string;
  confirmation_date: string | null;
  created_by: { id: string; email: string };
  is_live: boolean;
  can_resend: boolean;
  has_profile: boolean;
  has_verification: boolean;
  stamp: { kind: 'verified' | 'received' | 'revoked' | 'closed'; at: string } | null;
};

export type RecipientProfileRowType = { key: string; label: string; value: string; sensitive: boolean };

export type RecipientRequestDetailsType = RecipientRequestType & {
  profile: {
    filled_by: 'recipient' | 'proxy';
    submitted_at: string;
    confirmation_channel: '' | 'email' | 'phone';
    confirmation_contact: string;
    save_to_own_vault: boolean;
    record_hash: string;
    blockchain_id: string;
    bc_status: 'pending' | 'success' | 'failed';
    rows: RecipientProfileRowType[];
  } | null;
};

export type RecipientRequestVerificationType = SecureLinkVerificationType;

export type PublicRecipientRequestStateType = {
  state: RequestStatusType;
  organization?: string;
  recipient_name?: string;
  request_type?: RequestTypeType;
  template_label?: string;
  template_draft?: boolean;
  fields?: TemplateFieldMetaType[];
  requires_verification?: boolean;
  audience?: RequestAudienceType;
  opened_as_invited_recipient?: boolean;
  wire_confirmation?: boolean;
  expires_at?: string;
};

export type RecipientRequestVerifyStatusType = {
  status: 'not_initialized' | 'initialized' | 'in_progress' | 'failed' | 'succeed';
  phone_masked?: string;
  start_new_session_enable_after_sec: number;
  session_id: string | null;
  can_resend: boolean;
};
